import { Tooltip, TooltipTrigger } from "@components";
import { HelpCircle, HelpSquare, SearchLg } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { PaginationPageDefault } from "@/components/application/pagination/pagination";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import ModalBase from "@/components/ui/modal/modal-base";
import { useToast } from "@/providers/toast-provider";
import { formularioBuscarPergunta } from "@/services/analytics/events";
import { QuestionType } from "@/types/question";
import { cx } from "@/utils/cx";
import { getQuestionTypeIcon, questionTypes } from "@/utils/questionIcons";
import { useChecklistQuestionsList } from "../hooks";
import type { ChecklistQuestion } from "../types";

type AddQuestionModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onConfirm?: (selectedItems: number[]) => void;
	initialSelectedIds?: number[];
};

const PAGE_SIZE = 12;

export const AddQuestionModal = ({
	isOpen,
	onClose,
	onConfirm,
	initialSelectedIds,
}: AddQuestionModalProps) => {
	const { showToast } = useToast();
	const [selectedItems, setSelectedItems] = useState<number[]>(
		initialSelectedIds ?? []
	);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
	const [selectedType, setSelectedType] = useState<QuestionType | null>(null);
	const [selectKey, setSelectKey] = useState(0);

	useEffect(() => {
		if (debouncedSearchTerm) {
			formularioBuscarPergunta(debouncedSearchTerm);
		}
	}, [debouncedSearchTerm]);

	const validQuestionTypes = questionTypes.filter(
		(type) =>
			Object.values(QuestionType).includes(type.id as QuestionType) ||
			type.id === 999
	);

	const getTypeForApi = (): string | undefined => {
		if (selectedType === null) {
			return;
		}
		// Checklist Padrão (999) é mapeado para Option (0) no backend
		if (selectedType === 999) {
			return QuestionType.Option.toString();
		}
		return selectedType.toString();
	};

	const { data, isLoading, isError } = useChecklistQuestionsList({
		prompt: debouncedSearchTerm || undefined,
		type: getTypeForApi(),
		// Checklist Padrão usa optionDisplayType: 1 (Icon)
		optionDisplayType: selectedType === 999 ? 1 : 2,
		pageNumber: currentPage,
		pageSize: PAGE_SIZE,
	});

	const availableQuestions = data?.items ?? [];
	const totalPages = data?.totalPages ?? 1;

	const toggleItem = (id: number) => {
		setSelectedItems((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
		);
	};

	const handleConfirmSelection = () => {
		if (!selectedItems.length) {
			showToast({
				variant: "error",
				title: "Nenhuma pergunta selecionada",
				description:
					"Selecione pelo menos uma pergunta para adicionar ao formulário.",
			});
			return;
		}

		showToast({ variant: "success", title: "Perguntas adicionadas!" });
		onConfirm?.(selectedItems);
		onClose();
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1);
		}, 500);
		return () => clearTimeout(handler);
	}, [searchTerm]);

	useEffect(() => {
		if (isOpen) {
			setSelectedItems(initialSelectedIds ?? []);
			setCurrentPage(1);
		}
	}, [isOpen, initialSelectedIds]);

	useEffect(() => {
		if (isError) {
			showToast({
				variant: "error",
				title: "Erro ao carregar perguntas",
				description: "Não foi possível carregar as perguntas. Tente novamente.",
			});
		}
	}, [isError, showToast]);

	const renderQuestion = (item: ChecklistQuestion) => {
		const isSelected = selectedItems.includes(item.id);
		const Icon = getQuestionTypeIcon(item.questionType);

		return (
			<Tooltip
				placement="top"
				style={{ wordBreak: "break-word" }}
				title={item.prompt}
			>
				<TooltipTrigger
					aria-label={item.prompt}
					className={cx(
						"flex h-auto w-full cursor-pointer flex-row items-center gap-2.5 rounded-lg border border-transparent p-3 transition hover:bg-gray-50",
						!!isSelected && "border-blue-500 bg-blue-100"
					)}
					key={item.id}
					onClick={() => toggleItem(item.id)}
					type="button"
				>
					<div
						className={`rounded-sm p-1.5 ${
							isSelected
								? "bg-blue-200 text-blue-700"
								: "bg-gray-200 text-gray-500"
						}`}
					>
						<Icon size={24} />
					</div>
					<span
						className={cx(
							"truncate font-medium text-gray-600 text-sm",
							!!isSelected && "text-blue-900"
						)}
					>
						{item.prompt}
					</span>
				</TooltipTrigger>
			</Tooltip>
		);
	};

	return (
		<ModalBase
			actions={[
				{ label: "Cancelar", color: "secondary", onClick: onClose },
				{
					label: `Adicionar pergunta (${selectedItems.length})`,
					color: "primary",
					onClick: handleConfirmSelection,
				},
			]}
			description="Selecione as perguntas que você deseja adicionar ao formulário"
			height={692}
			icon={<HelpCircle />}
			iconColor="brand"
			isOpen={isOpen}
			onClose={onClose}
			title="Adicionar pergunta"
			width={912}
		>
			<div className="flex h-full flex-col">
				<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
					<Input
						aria-label="Pesquisar pergunta"
						className="w-full sm:w-[432px]"
						icon={SearchLg}
						onChange={(value) => value.length <= 50 && setSearchTerm(value)}
						placeholder="Pesquise pela pergunta..."
						value={searchTerm}
					/>

					<Select
						aria-label="Tipo de pergunta"
						className="w-[200px] min-w-[200px] sm:w-auto"
						key={selectKey}
						onSelectionChange={(key) => {
							if (key === "all") {
								setSelectedType(null);
							} else {
								const newType = Number(key) as QuestionType;
								setSelectedType((prev) => {
									if (prev === newType) {
										return null;
									}
									return newType;
								});
							}
							setCurrentPage(1);
							setSelectKey((prev) => prev + 1);
						}}
						placeholder="Tipo de pergunta"
						placeholderIcon={HelpSquare}
						selectedKey={selectedType?.toString() ?? "all"}
					>
						<Select.Item icon={HelpSquare} id="all" key="all">
							Todos os tipos
						</Select.Item>
						{validQuestionTypes.map((type) => (
							<Select.Item
								icon={type.icon as any}
								id={type.id.toString()}
								key={type.id.toString()}
							>
								{type.label}
							</Select.Item>
						))}
					</Select>
				</div>

				<div className="flex flex-1 flex-col overflow-y-auto">
					{isLoading ? (
						<div className="flex h-full items-center justify-center text-gray-500">
							Carregando perguntas...
						</div>
					) : null}

					{!isLoading && availableQuestions.length > 0 && (
						<div className="flex h-full flex-col">
							<div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3">
								{availableQuestions.map(renderQuestion)}
							</div>
							<div className="mt-auto pb-4">
								<PaginationPageDefault
									onPageChange={(page) => {
										if (page === currentPage || page < 1 || page > totalPages) {
											return;
										}
										setCurrentPage(page);
									}}
									page={currentPage}
									total={totalPages}
								/>
							</div>
						</div>
					)}

					{!isLoading && availableQuestions.length === 0 && (
						<div className="flex h-full items-center justify-center text-gray-500">
							Nenhuma pergunta encontrada.
						</div>
					)}
				</div>
			</div>
		</ModalBase>
	);
};
